import { createContext, useContext } from 'react';
import type { TaskStatus } from '../../../lib/tasksApi';

/**
 * CLE-179 — shared drag-and-drop state for the Linear task list.
 *
 * `LinearTaskList` owns the live drag state and exposes it (plus the row/header
 * event handlers) through this context so `TaskRow` and the group header can
 * render the in-flight affordances — dragged-row dimming, the blue insertion
 * line, and the "Drop to move to {Status}" header highlight — without prop
 * drilling. Native HTML5 drag-and-drop is used (no extra dependency); the
 * dragged payload is the set of selected task ids (multi-drag) or the single
 * dragged row.
 */

export interface DropPosition {
  /** Group the cursor is currently over. */
  status: TaskStatus;
  /** Id of the row the insertion line sits *before*, or null = end of group. */
  beforeId: string | null;
}

export interface DragState {
  /** True while a drag is in progress. */
  active: boolean;
  /** Ids being dragged (>1 when multiple rows are selected). */
  draggingIds: Set<string>;
  /** Current drop target, or null when not over a valid zone. */
  drop: DropPosition | null;
  /** Status the dragged rows started in (used to detect cross-group moves). */
  sourceStatus: TaskStatus | null;
}

export interface DragApi {
  state: DragState;
  /** Begin dragging a row; returns the ids that will move. */
  onRowDragStart: (taskId: string, status: TaskStatus, e: React.DragEvent) => void;
  onRowDragEnd: () => void;
  /** Hover a row to set the insertion line position. */
  onRowDragOver: (taskId: string, status: TaskStatus, e: React.DragEvent) => void;
  /** Hover a group (header or empty area) to target the end of that group. */
  onGroupDragOver: (status: TaskStatus, e: React.DragEvent) => void;
  /** Drop onto the current target. */
  onDrop: (status: TaskStatus) => void;
  /** Whether a given status is a legal drop target for the current drag. */
  canDropOn: (status: TaskStatus) => boolean;
}

const noopState: DragState = {
  active: false,
  draggingIds: new Set(),
  drop: null,
  sourceStatus: null,
};

export const DragContext = createContext<DragApi>({
  state: noopState,
  onRowDragStart: () => {},
  onRowDragEnd: () => {},
  onRowDragOver: () => {},
  onGroupDragOver: () => {},
  onDrop: () => {},
  canDropOn: () => true,
});

export function useDrag(): DragApi {
  return useContext(DragContext);
}
